import clsx from "clsx";
import { Radio, RadioGroup, Label } from "@headlessui/react";
import { CheckCircleIcon } from "@phosphor-icons/react";

interface RadioOption {
	id: string | number;
	value: string;
	label?: string;
	icon?: React.ReactNode;
}

interface RadioGroupFieldProps {
	id: string;
	label: string;
	value: string;
	options: RadioOption[];
	onChange: (value: string) => void;
	required?: boolean;
	error?: string;
	disabled?: boolean;
	className?: string;
	gridCols?: number;
	showIcons?: boolean;
	disabledOptions?: string[];
	navRef?: boolean;
}

export function RadioGroupField({
	id,
	label,
	value,
	options,
	onChange,
	error,
	required = false,
	disabled = false,
	className = "",
	gridCols = 2,
	showIcons = false,
	disabledOptions = [],
	navRef = false,
}: RadioGroupFieldProps) {
	const colClass = {
		1: "eL-grid-cols-1",
		2: "eL-grid-cols-2",
		3: "eL-grid-cols-2 md:eL-grid-cols-3",
		4: "eL-grid-cols-2 md:eL-grid-cols-4",
		5: "eL-grid-cols-2 md:eL-grid-cols-5",
		6: "eL-grid-cols-2 md:eL-grid-cols-6",
		7: "eL-grid-cols-2 md:eL-grid-cols-7",
		8: "eL-grid-cols-4 md:eL-grid-cols-8",
	}[gridCols];

	return (
		<div className={className}>
			<RadioGroup
				id={id}
				aria-label={label}
				value={value}
				onChange={onChange}
				disabled={disabled}
				aria-required={required}
				aria-describedby={error ? `${id}-error` : ""}
				aria-invalid={!error}
			>
				<Label className="eL-text-base eL-font-semibold eL-leading-6 eL-text-gray-900">
					{label}
				</Label>
				<div
					style={{ gridAutoRows: "1fr" }}
					className={clsx(
						navRef ? "eL-grid-cols-2" : colClass,
						`eL-grid eL-mt-2
             eL-gap-x-2 eL-gap-y-2 md:eL-gap-x-2`,
					)}
				>
					{options.map((item) => {
						const isOptionDisabled = disabledOptions.includes(item.value);
						const optionId = `${id}-option-${item.id}`;
						return (
							<Radio
								key={item.value}
								value={item.value}
								disabled={isOptionDisabled}
								id={optionId}
								aria-describedby={
									isOptionDisabled ? `${optionId}-disabled` : undefined
								}
								className={({ checked }) =>
									clsx(
										"eL-h-full eL-flex-1 eL-min-h-11",
										disabled || isOptionDisabled
											? "eL-opacity-50 eL-cursor-not-allowed"
											: "eL-cursor-pointer",
										isOptionDisabled ? "eL-bg-gray-50" : "eL-bg-white",
										checked && !error
											? "eL-border-[--eL-theme-color] eL-ring-1 eL-ring-[--eL-theme-color]"
											: "",
										error
											? "eL-border-red-600"
											: !checked
												? "eL-border-gray-300"
												: "",
										showIcons ? "eL-flex-1" : "eL-justify-center",
										"eL-relative eL-flex eL-rounded-lg eL-border eL-p-2 eL-shadow-sm focus:eL-outline-none focus-visible:eL-outline-[--eL-theme-color] md:eL-p-3",
									)
								}
							>
								{({ checked }) => (
									<>
										<span
											className={clsx(
												showIcons ? "eL-flex eL-flex-1" : "eL-flex",
											)}
										>
											<span
												className={clsx(
													navRef ? "" : "",
													showIcons ? "eL-items-start" : "",
													"eL-flex eL-flex-col eL-justify-center",
												)}
											>
												{showIcons && item.icon && (
													<div
														className={clsx(
															!checked
																? " eL-text-gray-900"
																: "eL-text-[--eL-theme-color]",
														)}
													>
														{item.icon}
													</div>
												)}
												<Label
													as="span"
													className={clsx(
														!checked ? "eL-flex eL-text-gray-900" : "",
														"eL-text-wrap eL-text-[--eL-theme-color] eL-flex eL-text-sm eL-leading-4",
													)}
												>
													{item.label || item.value}
												</Label>
											</span>
										</span>
										<CheckCircleIcon
											className={clsx(
												!checked ? "eL-invisible" : "",
												"eL-h-5 eL-w-5 eL-text-[--eL-theme-color] eL-absolute eL-bg-white -eL-right-1 -eL-top-1",
											)}
											aria-hidden="true"
											weight="fill"
										/>
										<span
											className={clsx(
												checked
													? "eL-border-[--eL-theme-color]"
													: "eL-border-transparent",

												"eL-pointer-events-none eL-absolute eL-rounded-lg",
											)}
											aria-hidden="true"
										/>
									</>
								)}
							</Radio>
						);
					})}
				</div>
			</RadioGroup>
			{error && (
				<p
					id={`${id}-error`}
					className="eL-mt-1 eL-text-xs eL-text-red-600"
					role="alert"
					aria-live="polite"
				>
					{error}
				</p>
			)}
		</div>
	);
}
